// Gameplay semantics of the declared game module (7.6F, ADR-0022,
// INTERACTION-MODEL §7.5). Pure and deterministic, like `timeline.ts` and
// `expr.ts`: the Studio preview uses it today and the Delivery Runtime (7.6H)
// must use THE SAME functions — never a reimplementation.
//
// The session decides WHAT happens (a target is found, a pair matches, a drop
// is accepted, the game ends with an outcome) and returns it as a list of
// effects. The host (preview engine / runtime) applies them on its own logical
// clock: score/lives through the declared variables (clamped, with
// variable.changed), element states, scheduled settles, `game.event` rule
// triggers and the tx: 1 game_* measurement events. Nothing here reads a
// clock, the DOM, Math.random or the network.
//
// Randomness: one seeded PRNG per instance (`createRng`). The same seed and the
// same inputs reproduce a session exactly (preview, tests, QA). The document
// and the compiled artifact never contain random results.
//
// Geometry: axis-aligned rectangles in the coordinates of the active format
// variant (top-level layers; rotation is not considered in V1). No physics.

import { RunnerSession, type RunnerGeometry } from "./runner.js";
import type { CreativeDocument, GameDecl, GameOutcome, GameSignal } from "./schema/document.js";

// ─── Randomness ──────────────────────────────────────────────────────────────

/** Instance PRNG (mulberry32): deterministic for a seed, uniform in [0, 1). */
export function createRng(seed: number): () => number {
  let a = Math.trunc(Number.isFinite(seed) ? seed : 0) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/** Fisher–Yates with the instance RNG; returns a new array. */
export function shuffled<T>(list: readonly T[], random: () => number): T[] {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j] as T, out[i] as T];
  }
  return out;
}

// ─── Geometry ────────────────────────────────────────────────────────────────

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}
export interface Point {
  readonly x: number;
  readonly y: number;
}

export const rectCenter = (r: Rect): Point => ({ x: r.x + r.width / 2, y: r.y + r.height / 2 });

/** Inclusive on the top/left edges, exclusive on the bottom/right ones (no double hits on shared edges). */
export const pointInRect = (p: Point, r: Rect): boolean =>
  p.x >= r.x && p.y >= r.y && p.x < r.x + r.width && p.y < r.y + r.height;

/** Strict overlap (touching edges do not overlap). */
export const rectsOverlap = (a: Rect, b: Rect): boolean =>
  a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;

/** Topmost zone (last in the given back-to-front order) that contains the point. */
export function zoneAt(
  point: Point,
  zones: ReadonlyArray<{ readonly id: string; readonly rect: Rect }>
): string | null {
  for (let i = zones.length - 1; i >= 0; i--) {
    const z = zones[i];
    if (z && pointInRect(point, z.rect)) return z.id;
  }
  return null;
}

/**
 * Random top-left position for a box of `size` fully inside `area` (integer px).
 * A box larger than the area is centered on it on that axis (never placed outside at random).
 */
export function randomPlacement(
  size: { readonly width: number; readonly height: number },
  area: Rect,
  random: () => number
): Point {
  const axis = (start: number, room: number, extent: number) => {
    const free = room - extent;
    return free <= 0 ? Math.round(start + free / 2) : Math.round(start + random() * free);
  };
  return { x: axis(area.x, area.width, size.width), y: axis(area.y, area.height, size.height) };
}

/** Item frame snapped so its center is the zone's center. */
export function snapToCenter(item: Rect, zone: Rect): Point {
  const c = rectCenter(zone);
  return { x: Math.round(c.x - item.width / 2), y: Math.round(c.y - item.height / 2) };
}

// ─── Session ─────────────────────────────────────────────────────────────────

export type GamePhase = "idle" | "running" | "ended";
export type CardFace = "down" | "up" | "matched";
export type GameInteractionKind = "tap" | "drag";

export type GameEffect =
  /** Add to the score variable (host clamps to its declared range). */
  | { readonly kind: "score"; readonly delta: number }
  /** Add to the lives variable (a runner pickup may add; the host clamps to its declared range). */
  | { readonly kind: "lives"; readonly delta: number }
  /** Add to the runner's energy variable (host clamps to its declared range; 7.6F.1). */
  | { readonly kind: "energy"; readonly delta: number }
  | { readonly kind: "state"; readonly target: string; readonly state: string }
  /** Move a target to a random position inside the area (host resolves frames + RNG). */
  | { readonly kind: "relocate"; readonly target: string; readonly area: string }
  /** Call `settleMismatch()` after `ms` on the host's logical clock (cancelled by end/restart). */
  | { readonly kind: "settle"; readonly ms: number }
  /** Item accepted: draw it snapped onto the zone. */
  | { readonly kind: "snap"; readonly item: string; readonly zone: string }
  /** Item returns to where it started. */
  | { readonly kind: "return"; readonly item: string }
  /** Rule trigger `game.event` (derived occurrence; `gesture` = caused directly by the player). */
  | { readonly kind: "signal"; readonly event: GameSignal; readonly target?: string; readonly gesture: boolean }
  /** Counted for the aggregated `game_interaction` measurement (never one event per tap). */
  | { readonly kind: "interaction"; readonly interaction: GameInteractionKind };

export interface GameReadings {
  readonly score: number;
  readonly lives: number | undefined;
  /** Runner energy variable value (7.6F.1); undefined = no energy declared. */
  readonly energy?: number | undefined;
}

/**
 * Lowest value of the runner's energy variable (its declared minimum): reaching it loses.
 * Read from the declaration, never chosen by the host.
 */
export function runnerEnergyFloor(doc: CreativeDocument): number | undefined {
  const id = doc.game?.runner?.energy?.var;
  const v = id === undefined ? undefined : doc.variables?.find((x) => x.id === id);
  return v?.type === "number" ? (v.min ?? 0) : undefined;
}

/**
 * State machine of one game in one ad instance and one format variant.
 * `present(id)` says whether an element exists in the active variant: only
 * present objectives count, so every variant can be cleared.
 */
export class GameSession {
  readonly decl: GameDecl;
  #phase: GamePhase = "idle";
  #outcome: GameOutcome | null = null;
  readonly #present: (id: string) => boolean;
  readonly #targets: Map<string, NonNullable<GameDecl["targets"]>[number]>;
  readonly #pairOf = new Map<string, string>();
  readonly #found = new Set<string>();
  readonly #matched = new Set<string>();
  #faceUp: string[] = [];
  #settling = false;
  readonly #placed = new Map<string, string>();
  #slots = new Map<string, string>();

  /** Side-scrolling runner of this game (7.6F.1); null when the module declares none or no geometry was given. */
  readonly runner: RunnerSession | null;
  readonly #energyFloor: number | undefined;

  constructor(
    decl: GameDecl,
    present: (id: string) => boolean = () => true,
    runner?: { readonly geometry: RunnerGeometry; readonly energyFloor?: number | undefined }
  ) {
    this.decl = decl;
    this.#present = present;
    this.runner = decl.runner && runner ? new RunnerSession(decl.runner, runner.geometry, present) : null;
    this.#energyFloor = runner?.energyFloor;
    this.#targets = new Map((decl.targets ?? []).map((t) => [t.element, t]));
    for (const [a, b] of decl.pairs?.cards ?? []) {
      if (a === undefined || b === undefined) continue;
      this.#pairOf.set(a, b);
      this.#pairOf.set(b, a);
    }
  }

  get phase(): GamePhase {
    return this.#phase;
  }
  get outcome(): GameOutcome | null {
    return this.#outcome;
  }
  get running(): boolean {
    return this.#phase === "running";
  }

  // Queries (renderer, accessibility).
  isTarget(id: string): boolean {
    return this.#targets.has(id) && this.#present(id);
  }
  isCard(id: string): boolean {
    return this.#pairOf.has(id) && this.#present(id);
  }
  isItem(id: string): boolean {
    return (this.decl.drops?.items.includes(id) ?? false) && this.#present(id);
  }
  isZone(id: string): boolean {
    return (this.decl.drops?.zones.includes(id) ?? false) && this.#present(id);
  }
  isFound(id: string): boolean {
    return this.#found.has(id);
  }
  cardFace(id: string): CardFace {
    if (this.#matched.has(id)) return "matched";
    return this.#faceUp.includes(id) ? "up" : "down";
  }
  /** The card whose frame (slot) this card occupies after the shuffle; itself when not shuffled. */
  cardSlot(id: string): string {
    return this.#slots.get(id) ?? id;
  }
  placedZone(item: string): string | undefined {
    return this.#placed.get(item);
  }
  /** One-time correct targets, cards and drop items present in the variant. */
  #objectives(): string[] {
    return [
      ...(this.decl.targets ?? []).filter((t) => t.role === "correct" && t.repeat !== true).map((t) => t.element),
      ...(this.decl.pairs?.cards.flat() ?? []),
      ...(this.decl.drops?.items ?? []),
    ].filter((id) => this.#present(id));
  }
  /** Objectives present in the variant and not yet done (found / matched / placed). */
  remaining(): number {
    return this.#objectives().filter((id) => !this.#found.has(id) && !this.#matched.has(id) && !this.#placed.has(id))
      .length;
  }
  total(): number {
    return this.#objectives().length;
  }
  /** Pairs present in the variant, matched pairs so far. */
  pairsProgress(): { readonly matched: number; readonly total: number } {
    const present = (this.decl.pairs?.cards ?? []).filter(([a]) => a !== undefined && this.#present(a));
    return { matched: present.filter(([a]) => a !== undefined && this.#matched.has(a)).length, total: present.length };
  }

  // Lifecycle.

  /** Starts (idle → running): clears the mechanics and, if declared, shuffles the cards with the RNG. */
  start(random: () => number): GameEffect[] {
    if (this.#phase !== "idle") return [];
    this.#clear();
    if (this.decl.pairs?.shuffle) {
      const cards = this.decl.pairs.cards.flatMap((p) => [...p]).filter((c) => this.#present(c));
      const order = shuffled(cards, random);
      this.#slots = new Map(cards.map((c, i) => [c, order[i] as string]));
    }
    this.#phase = "running";
    this.runner?.start(random);
    return [{ kind: "signal", event: "start", gesture: false }];
  }

  // Runner inputs and the fixed step (7.6F.1): only while the round runs.
  runnerJump(): GameEffect[] {
    return this.running ? (this.runner?.jump() ?? []) : [];
  }
  runnerDuck(pressed: boolean): GameEffect[] {
    return this.running ? (this.runner?.duck(pressed) ?? []) : [];
  }
  runnerStep(random: () => number): GameEffect[] {
    return this.running ? (this.runner?.step(random) ?? []) : [];
  }

  /** Back to idle (game.restart): everything the game did is undone; the host resets variables/timers. */
  reset(): void {
    this.#clear();
    this.runner?.reset();
    this.#phase = "idle";
    this.#outcome = null;
  }

  #clear() {
    this.#found.clear();
    this.#matched.clear();
    this.#faceUp = [];
    this.#settling = false;
    this.#placed.clear();
    this.#slots = new Map();
  }

  /** Ends the game once (running → ended). Returns the outcome signal; the host freezes timers. */
  end(outcome: GameOutcome): GameEffect[] {
    if (this.#phase !== "running") return [];
    this.#phase = "ended";
    this.#outcome = outcome;
    this.#settling = false;
    this.runner?.end();
    const signal: GameSignal | null =
      outcome === "win" ? "win" : outcome === "lose" ? "lose" : outcome === "timeout" ? "timeout" : null;
    return signal ? [{ kind: "signal", event: signal, gesture: false }] : [];
  }

  // Inputs (only while running; anything else is ignored, never an error).

  tapTarget(id: string): GameEffect[] {
    const t = this.#targets.get(id);
    if (!this.running || !t || !this.#present(id)) return [];
    const { scoring } = this.decl;
    const out: GameEffect[] = [{ kind: "interaction", interaction: "tap" }];
    if (t.role === "wrong") {
      out.push(...this.#mistake(t.points ?? scoring.miss));
      if (t.relocate !== undefined) out.push({ kind: "relocate", target: id, area: t.relocate });
      out.push({ kind: "signal", event: "miss", target: id, gesture: true });
      return out;
    }
    if (t.repeat !== true) {
      if (this.#found.has(id)) return []; // already found: the tap does nothing
      this.#found.add(id);
      if (t.foundState !== undefined) out.push({ kind: "state", target: id, state: t.foundState });
    }
    const pts = t.points ?? scoring.hit;
    if (pts !== 0) out.push({ kind: "score", delta: pts });
    if (t.relocate !== undefined) out.push({ kind: "relocate", target: id, area: t.relocate });
    out.push({ kind: "signal", event: "hit", target: id, gesture: true });
    return out;
  }

  tapCard(id: string): GameEffect[] {
    const pairs = this.decl.pairs;
    if (!this.running || !pairs || !this.isCard(id) || this.#settling) return [];
    if (this.#matched.has(id) || this.#faceUp.includes(id)) return [];
    this.#faceUp.push(id);
    const out: GameEffect[] = [{ kind: "interaction", interaction: "tap" }];
    if (this.#faceUp.length < 2) return out;
    const [first, second] = this.#faceUp as [string, string];
    if (this.#pairOf.get(first) === second) {
      this.#matched.add(first);
      this.#matched.add(second);
      this.#faceUp = [];
      if (pairs.matchedState !== undefined)
        out.push(
          { kind: "state", target: first, state: pairs.matchedState },
          { kind: "state", target: second, state: pairs.matchedState }
        );
      if (this.decl.scoring.hit !== 0) out.push({ kind: "score", delta: this.decl.scoring.hit });
      out.push({ kind: "signal", event: "match", target: second, gesture: true });
      return out;
    }
    this.#settling = true;
    out.push(...this.#mistake(this.decl.scoring.miss));
    out.push({ kind: "signal", event: "mismatch", target: second, gesture: true });
    out.push({ kind: "settle", ms: pairs.mismatchMs });
    return out;
  }

  /** After a mismatch delay: both cards turn face down again. */
  settleMismatch(): GameEffect[] {
    if (!this.#settling) return [];
    this.#settling = false;
    this.#faceUp = [];
    return [];
  }

  /**
   * An item was released over `zone` (null = no zone). `accepts` = the item's
   * declared drop targets. Accepted → placed for good; a zone that does not
   * accept it → rejected (penalty) and returned; no zone → returned, no penalty.
   */
  drop(item: string, zone: string | null, accepts: readonly string[]): GameEffect[] {
    if (!this.running || !this.isItem(item) || this.#placed.has(item)) return [];
    const out: GameEffect[] = [{ kind: "interaction", interaction: "drag" }];
    if (zone === null || !this.isZone(zone)) {
      out.push({ kind: "return", item });
      return out;
    }
    if (accepts.includes(zone)) {
      this.#placed.set(item, zone);
      out.push({ kind: "snap", item, zone });
      if (this.decl.scoring.hit !== 0) out.push({ kind: "score", delta: this.decl.scoring.hit });
      out.push({ kind: "signal", event: "drop", target: item, gesture: true });
      return out;
    }
    out.push({ kind: "return", item });
    out.push(...this.#mistake(this.decl.scoring.miss));
    out.push({ kind: "signal", event: "reject", target: item, gesture: true });
    return out;
  }

  #mistake(points: number): GameEffect[] {
    const out: GameEffect[] = [];
    if (points !== 0) out.push({ kind: "score", delta: points });
    if (this.decl.scoring.missCostsLife && this.decl.lives !== undefined) out.push({ kind: "lives", delta: -1 });
    return out;
  }

  // Outcome (evaluated by the host after every run-to-completion step).

  #winHolds(r: GameReadings): boolean {
    const win = this.decl.win;
    if (win.when === "cleared") return this.total() > 0 && this.remaining() === 0;
    if (win.when === "score") return r.score >= win.atLeast;
    if (win.when === "finish") return this.runner?.finished ?? false;
    return false;
  }

  /** The runner's energy reached its declared minimum. */
  #outOfEnergy(r: GameReadings): boolean {
    return (
      this.runner !== null && this.#energyFloor !== undefined && r.energy !== undefined && r.energy <= this.#energyFloor
    );
  }

  /**
   * Immediate end, if any: the win condition first (cleared, an immediate
   * score goal, the runner's finish line), then lives at 0, then the runner's
   * energy at its minimum. Deterministic precedence: win before lose.
   */
  checkEnd(r: GameReadings): GameOutcome | null {
    if (!this.running) return null;
    const win = this.decl.win;
    if ((win.when === "cleared" || win.when === "finish") && this.#winHolds(r)) return "win";
    if (win.when === "score" && win.immediate && this.#winHolds(r)) return "win";
    if (this.decl.lives !== undefined && r.lives !== undefined && r.lives <= 0) return "lose";
    if (this.#outOfEnergy(r)) return "lose";
    return null;
  }

  /** The time limit ended: surviving or a reached score goal wins; otherwise the game timed out. */
  timeUp(r: GameReadings): GameOutcome {
    const win = this.decl.win;
    if (win.when === "survive") return "win";
    return this.#winHolds(r) ? "win" : "timeout";
  }

  /** `game.end { outcome: "auto" }`: the declared win condition decides. */
  resolveAuto(r: GameReadings): GameOutcome {
    const win = this.decl.win;
    if (win.when === "manual") return "neutral";
    if (win.when === "survive") return (r.lives === undefined || r.lives > 0) && !this.#outOfEnergy(r) ? "win" : "lose";
    return this.#winHolds(r) ? "win" : "lose";
  }

  /** Result scene for an outcome (timeout falls back to lose; neutral has none). */
  resultScene(outcome: GameOutcome): string | undefined {
    const r = this.decl.results;
    if (outcome === "win") return r.win;
    if (outcome === "lose") return r.lose;
    if (outcome === "timeout") return r.timeout ?? r.lose;
    return undefined;
  }
}
